import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, GlobeIcon, HeartIcon, SchoolIcon, ShieldIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
type CoverageItem = {
  icon: typeof SchoolIcon;
  title: string;
  description: string;
  tone: Tone;
};
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const coverage: CoverageItem[] = [{
  icon: SchoolIcon,
  title: 'التعامل مع المدارس والمؤسسات',
  description: 'كيف تتواصل مع المعلمين والمديرين والإداريين بهدوء ووضوح وفاعلية.',
  tone: 'sage'
}, {
  icon: GlobeIcon,
  title: 'فهم الاختلافات الثقافية',
  description: 'التعرّف إلى مواضع سوء الفهم وكيفية تجاوزها دون التنازل عن القيم.',
  tone: 'sky'
}, {
  icon: HeartIcon,
  title: 'أسئلة الهوية والمشاعر',
  description: 'دعم الأطفال عند مواجهة الأسئلة الصعبة حول الانتماء والإيمان والاختلاف.',
  tone: 'rose'
}, {
  icon: ShieldIcon,
  title: 'المرونة والثقة',
  description: 'بناء القوة العاطفية لدى الأطفال والوالدين: إرشاد بلا خوف وحماية بلا عزل.',
  tone: 'sand'
}];
const approachPoints = ['الإرشاد بلا خوف: فالثقة تُكتسب بالقدوة', 'الحماية بلا عزل: فالقوة تأتي من الانتماء لا الانسحاب', 'التواصل الهادئ مع المؤسسات: الكرامة قبل المواجهة', 'دعم أسئلة الهوية بصدق وبما يناسب عمر الطفل'];
export function ArabicParentingInNonMuslimCountries() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-4218" data-mp="1o944vzop1ku">
      <section className="relative isolate overflow-hidden" data-id="element-4219" data-mp="3ttcxmh2dmjwb">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-4220" data-mp="3ttwfjxs118ac" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-4221" data-mp="3o72jtfp03nty" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-4222" data-mp="3a7kt2us5a0hy">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-4223" data-mp="3a9e38k1yv2up">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-4224" data-mp="3f77899rj9qte">
              <span className="text-sm font-semibold text-forest-700" data-id="element-4225" data-mp="asphr1jzn2l2">فهم البيئة التي ينمو فيها طفلك</span>
              <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.45] text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-4226" data-mp="3t6yq5ll67rco">التربية في الدول غير المسلمة</h1>
              <div className="mt-6 max-w-xl space-y-3 leading-loose text-ink-700 md:text-lg" data-id="element-4227" data-mp="1xjady52kcgf">
                <p data-id="element-4228" data-mp="pnlyy49rz0aa">تربية الأطفال المسلمين في الدول غير المسلمة تتطلب وعيًا واستعدادًا وقوة عاطفية.</p>
                <p data-id="element-4229" data-mp="ui00kof163ne">يساعدك هذا البرنامج على التعامل مع المدارس والمؤسسات، وفهم الاختلافات الثقافية، ودعم الأطفال في أسئلة الهوية، وبناء المرونة والثقة — إرشادًا بلا خوف وحماية بلا عزل.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-4230" data-mp="1dksbbw0et4pg"><a href="#content" data-id="element-4231" data-mp="1djqoyvdy0vsf"><Button variant="primary" size="lg" data-id="element-4232" data-mp="1dka8ggj8rs8k">استكشف البرنامج</Button></a><Link to="/ar/parents/membership" data-id="element-4233" data-mp="18l746yb35q1n"><Button variant="secondary" size="lg" data-id="element-4234" data-mp="18m8q5xtn3wy5">عضوية الوالدين</Button></Link></div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-4235" data-mp="kwecje1hy4cu"><div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-4236" data-mp="uvvfhnpd6454"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/6d6KCRmKhV3Z3mUysSobLV/Parenting.jpg" alt="أسرة مسلمة تسير مع طفلها إلى المدرسة في شارع بالمدينة" data-id="element-4237" data-mp="pu6241eo5edv" /></div></motion.div>
          </div>
        </Container>
      </section>

      <section id="content" className="scroll-mt-24 bg-sage-50/60 py-16 md:py-24" data-id="element-4238" data-mp="21ch1b5prdh38">
        <Container data-id="element-4239" data-mp="av3m38x9v7bm">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4240" data-mp="avls2x9qkzwq">ما الذي يغطيه البرنامج</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-4241" data-mp="kqx61ddbm33f">إرشاد عملي للمواقف الواقعية التي يواجهها الوالدان المسلمون في المدارس والمؤسسات والمجتمع والحياة المنزلية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-4242" data-mp="ts4tt1i7nlj9">{coverage.map((item, index) => <CoverageCard key={item.title} item={item} index={index} data-id="element-4243" data-mp="tq0s8q7saym5" data-mpi={index} />)}</div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-4244" data-mp="yov8bkeda92k">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4245" data-mp="13k11n4a3mq0o" />
        <Container data-id="element-4246" data-mp="18fzlqfoe9lva">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-4247" data-mp="18ghrq40uzege">
            <div className="lg:col-span-6" data-id="element-4248" data-mp="1cje4he3pwgcf">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-4249" data-mp="1cgjihi522ngw">منهجنا</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-4250" data-mp="1hfd0nnvaf2ne">نساعد الوالدين على القيادة بوضوح وهدوء، لا بخوف. يمتص الأطفال الحالة العاطفية للبالغين من حولهم، ولذلك نركز على تزويد الوالدين بأدوات تجسّد الكرامة والثقة والتواصل المحترم.</p>
              <ul className="mt-6 space-y-3" data-id="element-4251" data-mp="2sya6udtg9lzm">{approachPoints.map(point => <li key={point} className="flex items-start gap-3 leading-loose text-ink-700" data-id="element-4252" data-mp="2t0wkmv6xoxqa"><CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-4253" data-mp="2o1tgdcys2vjd" /><span data-id="element-4254" data-mp="3ksc5oksuecee">{point}</span></li>)}</ul>
            </div>
            <div className="lg:col-span-6" data-id="element-4255" data-mp="3gppemm3zwvi9"><div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-4256" data-mp="3gqqzfiatcwd6"><img src="https://cdn.magicpatterns.com/uploads/rijTYZAoAnis9wjifPVz2x/img1.jpg" alt="أم مسلمة وطفلها يتحدثان بهدوء مع مسؤول في المدرسة" className="h-full w-full object-cover" data-id="element-4257" data-mp="3bwmravptaiai" /></div></div>
          </div>
        </Container>
      </section>

      <section className="bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-4258" data-mp="3i4dedd6w8iz">
        <Container data-id="element-4259" data-mp="3gjkes6iwxn8">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-4260" data-mp="3g1ef3u27524">
            <div className="max-w-xl" data-id="element-4261" data-mp="mgfysfvpdi2y"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4262" data-mp="rdg2wl8zsarf">أرشد أسرتك بوضوح</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-4263" data-mp="dmb3nk9jdkhm">استكشف عضوية الوالدين لتحصل على إرشاد مستمر وخبرة متخصصة ومجتمع من الوالدين المسلمين.</p></div>
            <div className="flex flex-wrap gap-3" data-id="element-4264" data-mp="hltn0blplnic"><Link to="/ar/waitlist" data-id="element-4265" data-mp="3o50z0fwoabp"><Button variant="primary" data-id="element-4266" data-mp="3nlr870ess8r">انضم إلى العضوية</Button></Link><Link to="/ar/parents/preventing-discrimination" data-id="element-4267" data-mp="3q7ys6nav7qz"><Button variant="secondary" data-id="element-4268" data-mp="8nzpwyg4epu4">الوقاية من التمييز</Button></Link></div>
          </div>
        </Container>
      </section>
    </div>;
}
function CoverageCard({
  item,
  index
}: {
  item: CoverageItem;
  index: number;
}) {
  const Icon = item.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`flex gap-4 rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-4269" data-mp="3uvzlew1vc291"><span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-4270" data-mp="3dldkydfe1hf0"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-4271" data-mp="3nanezytsla5r" /></span><div data-id="element-4272" data-mp="3nc8c9w7hr77b"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-4273" data-mp="3nbyh7u7la9ur">{item.title}</h3><p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-4274" data-mp="3iidpgjwlac40">{item.description}</p></div></motion.article>;
}